import React, { useEffect, useState } from "react";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, FIXED_COMPLIANT_LABEL, FIX_LATER_PARTIALLY_COMPLIANT_LABEL } from "src/constants/constants";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Column, Row, Text, Icon, Card, Loader } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { getInspectionWorkflow } from "src/store/workflowSlice";
import { verifyCheckpoint } from "src/store/inspectionResultsSlice";
import InspectionService, {
    InspectionData,
    InspectionResult,
    ObjectPresenceCheck,
    isFixedNow,
    isFixedLater,
} from "src/services/inspectionService";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import { inspectionFlowTimingLogger } from "src/services/inspectionFlowTimingLogger";
import CheckpointList from "./CheckpointList";
import { formatDate } from "src/utils/dateUtil";
import { isResultNA } from "src/components/pages/inspection/utils";
import { orderGroupedResultsByQuestion, isAnalysisPending, getVerificationSummary } from "../utils/resultGrouping";
import InspectionVerifySummaryFooter from "./InspectionVerifySummaryFooter";
import ErrorSheet from "src/components/UI/ErrorSheet";
import "./inspectionReviewPage.scss";

const POLL_INTERVAL_MS = 3000;

/** Generic, user-friendly message shown when submitting the inspection fails. */
const SUBMIT_ERROR_MESSAGE = "Something went wrong. Please try again.";

/** Check if any result across all groups is still pending */
const hasAnyPendingAnalysis = (
	groupedResults: Record<string, InspectionResult[]>
): boolean =>
	Object.values(groupedResults).some((results) =>
		results.some(isAnalysisPending)
	);

/** Determine overall compliance from grouped results */
const getOverallCompliance = (
    groupedResults: Record<string, InspectionResult[]>
): boolean => {
    return Object.values(groupedResults).every((results) => {
        const scoredResults = results.filter((r) => !isResultNA(r));
        return scoredResults.length === 0 || scoredResults.every((r) => r.isCompliant);
    }
    );
};

/** Whether any result or object check across all groups was fixed on site. */
const hasAnyFixNow = (
    groupedResults: Record<string, InspectionResult[]>
): boolean =>
    Object.values(groupedResults).some((results) =>
        results.some((r) =>
            isFixedNow(r) || (r.objectPresenceChecks ?? []).some(isFixedNow)
        )
    );

/** Whether any result or object check across all groups is deferred (fix-it-later). */
const hasAnyFixLater = (
    groupedResults: Record<string, InspectionResult[]>
): boolean =>
    Object.values(groupedResults).some((results) =>
        results.some((r) =>
            isFixedLater(r) || (r.objectPresenceChecks ?? []).some(isFixedLater)
        )
    );

/** An object check is acceptable for fix-later-partial when it is compliant or deferred. */
const isCheckAcceptable = (check: ObjectPresenceCheck): boolean =>
    check.is_compliant || isFixedLater(check);

/** A result is acceptable when all its object checks are, or (if none) it is compliant or deferred. */
const isResultAcceptable = (r: InspectionResult): boolean => {
    const checks = r.objectPresenceChecks ?? [];
    if (checks.length > 0) return checks.every(isCheckAcceptable);
    return r.isCompliant || isFixedLater(r);
};

/**
 * Overall "Fix Later - Partially Compliant": at least one checkpoint is deferred
 * (fix-it-later) and no checkpoint is non-compliant without being deferred.
 * A leaf (object check, or a plain result) is acceptable when it is compliant or
 * deferred; NA results are ignored, mirroring getOverallCompliance.
 */
const isOverallFixLaterPartiallyCompliant = (
    groupedResults: Record<string, InspectionResult[]>
): boolean => {
    if (!hasAnyFixLater(groupedResults)) return false;

    return Object.values(groupedResults).every((results) =>
        results.filter((r) => !isResultNA(r)).every(isResultAcceptable)
    );
};

/** The compliance label shown at the top of the review page. */
const getOverallComplianceLabel = (
    groupedResults: Record<string, InspectionResult[]>
): string => {
    // Fully compliant, but at least one checkpoint got there via a fix-it-now.
    if (getOverallCompliance(groupedResults)) {
        return hasAnyFixNow(groupedResults) ? FIXED_COMPLIANT_LABEL : COMPLIANT_LABEL;
    }
    // Not fully compliant, but the only non-compliant rows are deferred (fix-it-later).
    if (isOverallFixLaterPartiallyCompliant(groupedResults)) {
        return FIX_LATER_PARTIALLY_COMPLIANT_LABEL;
    }
    // A genuine non-compliant row remains.
    return NON_COMPLIANT_LABEL;
};

const InspectionReviewPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    // Optional deep link (?question=<id>) used by the SafetyGenie result links we
    // send to Austin, so an Austin reviewer lands on the specific question.
    const targetQuestionId = searchParams.get("question");
    // Only the fix/override return trip stamps &expand=1 (via anchorQuestion).
    // Austin deep links carry just ?question=<id>, so they stay scroll-only and
    // never auto-expand the detailed compliance list.
    const shouldExpand = searchParams.get("expand") === "1";
    const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
    const [presignedUrlMap, setPresignedUrlMap] = useState<Record<string, string>>({});
    const [groupedResults, setGroupedResults] = useState<Record<string, InspectionResult[]>>({});
    const [hasPendingResults, setHasPendingResults] = useState(true);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);
    // Question whose inline "Detailed compliance status" list should auto-expand
    // on return from a fix/override action (only when it still has an unresolved
    // non-compliant leaf). Null on plain Austin ?question= deep links.
    const [autoExpandQuestionId, setAutoExpandQuestionId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Human-readable submit failure message; null = no error / sheet closed.
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { questions } = useAppSelector((state) => state.workflow);

    // Fetch workflow config if not already loaded
    useEffect(() => {
        if (!questions || questions.length === 0) {
            dispatch(getInspectionWorkflow());
        }
    }, [questions]);

		// Fetches the inspection and updates local state. Shared by the mount/poll
		// effect and one-off refreshes (e.g. after verifying a checkpoint).
		const fetchInspection = async () => {
			try {
				const data = await InspectionService.fetchInspectionWithPresignedUrls(
					inspectionId!
				);
				setInspectionData(data.inspectionData);
				setPresignedUrlMap(data.presignedUrlMap);
				setGroupedResults(data.groupedResults);
				const stillPending = hasAnyPendingAnalysis(data.groupedResults);
				setHasPendingResults(stillPending);
				// Identifies this inspection for a resumed draft landing directly
				// here (no prior startInspection() call); no-op if already set.
				inspectionFlowTimingLogger.identifyInspection(inspectionId!, data.inspectionData.siteCode);
				if (!stillPending) {
					// Results are no longer pending analysis: the inspector can now
					// actually read/override them. Marks the start of the review
					// phase (a no-op if already marked, e.g. re-fetch after a
					// transient error).
					inspectionFlowTimingLogger.markReviewStart();
				}
			} catch (err: any) {
				console.error("Failed to fetch inspection data:", err);
				setHasPendingResults(false);
			}
		};

		// Fetch inspection data on mount, then poll while any result has pending AI analysis
		useEffect(() => {
			if(!hasPendingResults) return;

			fetchInspection();
			const intervalId = setInterval(fetchInspection, POLL_INTERVAL_MS);
			return () => clearInterval(intervalId);
		}, [inspectionId, hasPendingResults]);

    // Verify / un-verify, then re-fetch to pick up the new stamps.
    const handleVerify = async (questionId: string, verified: boolean) => {
        try {
            await dispatch(verifyCheckpoint({
                inspection_id: inspectionId!,
                question_id: questionId,
                verified,
            })).unwrap();
            await fetchInspection();
        } catch (err) {
            console.error("Failed to verify checkpoint:", err);
            throw err; // let the footer's optimistic tick roll back
        }
    };

    const submitInspection = async () => {
        // Guard against double submits while a request is already in flight.
        if (isSubmitting) return;

        // Locked until all checkpoints requiring verification are verified.
        const { requiredCount, allVerified } = getVerificationSummary(groupedResults, questions);
        if (requiredCount > 0 && !allVerified) return;

        // Locked while an unresolved non-compliant checkpoint remains.
        if (!getOverallCompliance(groupedResults) && !isOverallFixLaterPartiallyCompliant(groupedResults)) return;

        setIsSubmitting(true);
        // Closes out the review phase and starts timing the Austin/SafetyGenie
        // submission call itself.
        inspectionFlowTimingLogger.markSubmissionStart();
        try {
            // submit_inspection handles both cases: it submits to Austin/RCMS when the
            // inspection is linked to a scheduled Austin inspection, and simply completes
            // it in SafetyGenie otherwise.
            await InspectionService.submitInspection(inspectionId!);
            // Submission succeeded: this is the end of the whole timed flow
            // (Start Inspection click -> submission complete). Logs + emits
            // metrics, then resets for the next inspection.
            inspectionFlowTimingLogger.recordInspectionComplete();
            dispatch(resetInspectionCaptureState());
            navigate(`/configurationDrivenInspections/${inspectionId}/submitted`, {
                state: {
                    overallCompliant: getOverallCompliance(groupedResults),
                    isAustinLinked: Boolean(inspectionData?.austinInspectionId),
                },
            });
        } catch (error) {
            console.error("Failed to submit inspection:", error);
            // Re-enable the button and surface a retry sheet to the inspector.
            setIsSubmitting(false);
            // Show a generic, user-friendly message rather than the raw error, which
            // can be technical (e.g. "Request failed with status code 500").
            setSubmitError(SUBMIT_ERROR_MESSAGE);
        }
    };

    // Scroll to and briefly highlight the deep-linked question once results are rendered.
    // This is a ONE-SHOT action per deep-link target: after scrolling we clear the
    // question/expand params from the URL so later re-fetches (verifying another
    // question, or the ~3s poll) can't retrigger the scroll via the inspectionData
    // dependency. inspectionData stays in the deps only so we wait until results are
    // rendered and document.getElementById can find the question element.
    useEffect(() => {
        if (!targetQuestionId || hasPendingResults || !inspectionData) return;
        const el = document.getElementById(`question-${targetQuestionId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedQuestionId(targetQuestionId);
        // Signal auto-expand only on the fix/override return trip (expand=1). The
        // card itself decides whether to open (only if it still has an unresolved
        // non-compliant leaf). Austin links lack expand=1, so they stay scroll-only.
        if (shouldExpand) setAutoExpandQuestionId(targetQuestionId);
        // Clear the deep-link params so the stamp can't retrigger this effect on a
        // later re-fetch. This re-runs the effect once with targetQuestionId === null,
        // which early-returns at the guard above. highlightedQuestionId and
        // autoExpandQuestionId are separate state set above, so they persist.
        setSearchParams({}, { replace: true });
    }, [targetQuestionId, shouldExpand, hasPendingResults, inspectionData, setSearchParams]);

    const getQuestionText = (questionId: string): string => {
        const question = questions.find((q) => q.id === questionId);
        return question?.text || questionId;
    };

    // Stamp the current /review URL with ?question=<id> before leaving for a
    // fix/override page. The destination pages return via navigate(-1), so they
    // land back on /review?question=<id> and the deep-link scroll effect above
    // restores the user to the question they just acted on. { replace: true }
    // rewrites the current entry in place (no extra history entry).
    // expand=1 marks this as a fix/override return trip (as opposed to an Austin
    // deep link): on return, the card for this question auto-expands its inline
    // detailed list if it still has an unresolved non-compliant leaf.
    const anchorQuestion = (questionId: string) =>
        setSearchParams({ question: questionId, expand: "1" }, { replace: true });

    if (!inspectionData) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Loading inspection data...</Text>
            </Column>
        );
    }

    const isCompleted = inspectionData.status.toLowerCase() === "completed";

    const overallCompliant = getOverallCompliance(groupedResults);
    const overallComplianceLabel = getOverallComplianceLabel(groupedResults);
    const isFixLaterPartial = !overallCompliant && isOverallFixLaterPartiallyCompliant(groupedResults);
    const { datePart, timePart } = formatDate(inspectionData.createdAt);
    const questionEntries = orderGroupedResultsByQuestion(groupedResults, questions);

    // An unresolved non-compliant checkpoint (the "Non Compliant" heading state)
    // can't be submitted to Austin. Derives purely from groupedResults, so it's
    // immune to the workflow-questions load race a mode-based check would hit.
    const nonComplianceBlocksSubmit = !overallCompliant && !isFixLaterPartial;

    const verificationSummary = getVerificationSummary(groupedResults, questions);

    const showVerifySummary =
        !isCompleted && !hasPendingResults &&
        (verificationSummary.requiredCount > 0 || nonComplianceBlocksSubmit);

    const verificationBlocksSubmit =
        verificationSummary.requiredCount > 0 && !verificationSummary.allVerified;

    return (
        <div className="inspection-review-page pwa-scrollable">
            <Column spacingInset="500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="inspection-review-back-button"
                        onClick={() => {
                            dispatch(resetInspectionCaptureState());
                            navigate("/");
                        }}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="primary" />
                    </div>
                    <Text type="b300" color="primary">
                        Inspection Review
                    </Text>
                </Row>

                <Text type="h300" color="primary">
                    {inspectionData?.templateName ?? "Inspection"}
                </Text>

                <Card spacingInset="none">
                    <Column spacingInset="400" spacing="200">
                        <Row spacing="200">
                            <Text type="b200" color="primary">
                                {datePart}
                            </Text>
                            <Text type="b200" color="secondary">
                                {timePart}
                            </Text>
                        </Row>
                        <Text type="b100" color="primary">
                            OBSERVATION
                        </Text>
                        {hasPendingResults ? (
							<Row spacing="200" alignmentVertical="center" alignmentHorizontal="justify">
								<Text type="h300" color="secondary">
									Analyzing...
								</Text>
								<Loader size="small" type="circular" />
							</Row>
						) : (
							<Text
								type="h300"
								color={isFixLaterPartial ? "warning" : overallCompliant ? "success" : "error"}
							>
								{overallComplianceLabel}
							</Text>
						)}
                    </Column>
                    {showVerifySummary && (
                        <InspectionVerifySummaryFooter
                            allVerified={!verificationBlocksSubmit}
                            hasUnresolvedNonCompliance={nonComplianceBlocksSubmit}
                        />
                    )}
                </Card>

                <Text type="h200" color="primary">
                    Check points
                </Text>

                <CheckpointList
                    questionEntries={questionEntries}
                    questions={questions}
                    presignedUrlMap={presignedUrlMap}
                    highlightedQuestionId={highlightedQuestionId}
                    autoExpandQuestionId={autoExpandQuestionId}
                    onVerify={!isCompleted ? handleVerify : undefined}
                    override={
                        !isCompleted
                            ? {
                                onResult: (result, questionId) => {
                                    anchorQuestion(questionId);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/override`, {
                                        state: { result, questionText: getQuestionText(questionId), questionMode: questions.find((q) => q.id === questionId)?.mode },
                                    });
                                },
                                onObjectCheck: (firstResult, questionId, check) => {
                                    anchorQuestion(questionId);
                                    const baseQuestion = getQuestionText(questionId);
                                    const displayName = check.object_name.replace(/\s*sign\s*$/i, "").trim();
                                    const questionWithSign = baseQuestion.replace("?", ` (${displayName})?`);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/override`, {
                                        state: { result: firstResult, questionText: questionWithSign, objectCheck: check },
                                    });
                                },
                                onCount: (result, questionId) => {
                                    anchorQuestion(questionId);
                                    // Hazardous containers can't exceed the number of
                                    // containers actually inspected (distinct objectIds
                                    // across all results).
                                    const totalContainers = new Set(
                                        Object.values(groupedResults)
                                            .flat()
                                            .map((r) => r.objectId)
                                            .filter((id): id is string => !!id)
                                    ).size;
                                    navigate(`/configurationDrivenInspections/${inspectionId}/overrideCount`, {
                                        state: { result, questionText: getQuestionText(questionId), maxCount: totalContainers },
                                    });
                                },
                                onFixNowResult: (result, questionId) => {
                                    anchorQuestion(questionId);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/fixNow`, {
                                        state: { result, questionText: getQuestionText(questionId) },
                                    });
                                },
                                onFixNowObjectCheck: (firstResult, questionId, check) => {
                                    anchorQuestion(questionId);
                                    const baseQuestion = getQuestionText(questionId);
                                    const displayName = check.object_name.replace(/\s*sign\s*$/i, "").trim();
                                    const questionWithSign = baseQuestion.replace("?", ` (${displayName})?`);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/fixNow`, {
                                        state: { result: firstResult, questionText: questionWithSign, objectCheck: check },
                                    });
                                },
                                onFixLaterResult: (result, questionId) => {
                                    anchorQuestion(questionId);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/fixLater`, {
                                        state: { result, questionText: getQuestionText(questionId) },
                                    });
                                },
                                onFixLaterObjectCheck: (firstResult, questionId, check) => {
                                    anchorQuestion(questionId);
                                    const baseQuestion = getQuestionText(questionId);
                                    const displayName = check.object_name.replace(/\s*sign\s*$/i, "").trim();
                                    const questionWithSign = baseQuestion.replace("?", ` (${displayName})?`);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/fixLater`, {
                                        state: { result: firstResult, questionText: questionWithSign, objectCheck: check },
                                    });
                                },
                            }
                            : undefined
                    }
                />

                {hasPendingResults===false && inspectionData.status.toLowerCase() === "in_progress" && (
                    <button
                        className="inspection-review-page__submit-btn"
                        onClick={submitInspection}
                        disabled={hasPendingResults || isSubmitting || verificationBlocksSubmit || nonComplianceBlocksSubmit}
                    >
                        {isSubmitting
                            ? "Submitting..."
                            : inspectionData.austinInspectionId
                                ? "SUBMIT TO AUSTIN"
                                : "Submit Inspection"}
                    </button>
                )}
            </Column>

            <ErrorSheet
                open={submitError !== null}
                title="Couldn't submit inspection"
                message={SUBMIT_ERROR_MESSAGE}
                onClose={() => setSubmitError(null)}
                actions={[
                    {
                        label: "Try Again",
                        onClick: () => {
                            setSubmitError(null);
                            submitInspection();
                        },
                    },
                ]}
            />
        </div>
    );
};

export default InspectionReviewPage;
